import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Slider } from './ui/slider';
import { Switch } from './ui/switch';
import { toast } from 'sonner';
import { Save, RotateCcw, TrendingUp, TrendingDown, AlertTriangle, Settings2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const LEAGUES = [
  { id: 'la_liga', name: 'La Liga', flag: '🇪🇸' },
  { id: 'premier_league', name: 'Premier League', flag: '🏴󠁧󠁢󠁥󠁮󠁧󠁿' },
  { id: 'bundesliga', name: 'Bundesliga', flag: '🇩🇪' },
  { id: 'serie_a', name: 'Serie A', flag: '🇮🇹' },
  { id: 'ligue_1', name: 'Ligue 1', flag: '🇫🇷' },
  { id: 'champions_league', name: 'Champions League', flag: '🏆' },
  { id: 'europa_league', name: 'Europa League', flag: '🏆' },
  { id: 'mls', name: 'MLS', flag: '🇺🇸' },
  { id: 'liga_mx', name: 'Liga MX', flag: '🇲🇽' },
];

const MARKETS = [
  { id: 'match_result', name: '1X2', icon: '⚽' },
  { id: 'btts', name: 'BTTS', icon: '🎯' },
  { id: 'over_under', name: 'Over/Under', icon: '📊' },
  { id: 'double_chance', name: 'Doble Op.', icon: '🔄' },
];

export default function ExigenciaSection() {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedLeague, setSelectedLeague] = useState('premier_league');

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/exigencia-config`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        setConfig(data);
      } else {
        setConfig(getDefaultConfig());
      }
    } catch (error) {
      setConfig(getDefaultConfig());
    } finally {
      setLoading(false);
    }
  };

  const getDefaultConfig = () => {
    const defaultConfig = { leagues: {} };
    LEAGUES.forEach(league => {
      defaultConfig.leagues[league.id] = {
        enabled: true,
        exigencia_base: league.id === 'premier_league' ? 120 : 100,
        markets: {}
      };
      MARKETS.forEach(market => {
        defaultConfig.leagues[league.id].markets[market.id] = {
          enabled: market.id !== 'over_under', // Over/Under disabled by default
          exigencia: 100,
          min_odds: 1.55,
          min_value: 7,
        };
      });
    });
    return defaultConfig;
  };

  const saveConfig = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/exigencia-config`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(config)
      });
      
      if (res.ok) {
        toast.success('Configuración guardada');
      } else {
        toast.error('Error al guardar');
      }
    } catch (error) {
      toast.error('Error de conexión');
    } finally {
      setSaving(false);
    }
  };

  const updateLeagueExigencia = (leagueId, value) => {
    setConfig(prev => ({
      ...prev,
      leagues: {
        ...prev.leagues,
        [leagueId]: { ...prev.leagues[leagueId], exigencia_base: value }
      }
    }));
  };

  const updateLeagueEnabled = (leagueId, enabled) => {
    setConfig(prev => ({
      ...prev,
      leagues: {
        ...prev.leagues,
        [leagueId]: { ...prev.leagues[leagueId], enabled }
      }
    }));
  };

  const updateMarketConfig = (leagueId, marketId, field, value) => {
    setConfig(prev => ({
      ...prev,
      leagues: {
        ...prev.leagues,
        [leagueId]: {
          ...prev.leagues[leagueId],
          markets: {
            ...prev.leagues[leagueId].markets,
            [marketId]: { ...prev.leagues[leagueId].markets[marketId], [field]: value }
          }
        }
      }
    }));
  };

  const getExigenciaColor = (value) => {
    if (value < 90) return 'text-green-400';
    if (value <= 110) return 'text-yellow-400';
    return 'text-red-400';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-[#00E599]"></div>
      </div>
    );
  }

  const currentLeague = config?.leagues?.[selectedLeague];

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Settings2 className="h-5 w-5 text-[#00E599]" />
          <h2 className="text-lg font-bold text-white">Nivel de Exigencia</h2>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => { setConfig(getDefaultConfig()); toast.info('Restaurado'); }}
            className="border-zinc-700 text-zinc-300 h-8"
          >
            <RotateCcw className="h-3 w-3 mr-1" />
            Reset
          </Button>
          <Button
            size="sm"
            onClick={saveConfig}
            disabled={saving}
            className="bg-[#00E599] text-black hover:bg-[#00E599]/90 h-8"
          >
            <Save className="h-3 w-3 mr-1" />
            {saving ? '...' : 'Guardar'}
          </Button>
        </div>
      </div>

      {/* Info */}
      <div className="bg-zinc-800/50 rounded-lg p-3 flex items-start gap-2">
        <AlertTriangle className="h-4 w-4 text-yellow-500 mt-0.5 flex-shrink-0" />
        <p className="text-xs text-zinc-400">
          <span className="text-red-400">120%</span> = +20% más estricto (menos picks, más calidad). 
          <span className="text-green-400 ml-1">80%</span> = más permisivo.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Leagues */}
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardHeader className="py-3 px-4">
            <CardTitle className="text-sm text-white">Ligas</CardTitle>
          </CardHeader>
          <CardContent className="p-2 max-h-[400px] overflow-y-auto">
            {LEAGUES.map(league => {
              const lc = config?.leagues?.[league.id];
              return (
                <button
                  key={league.id}
                  onClick={() => setSelectedLeague(league.id)}
                  className={`w-full flex items-center justify-between p-2 rounded-lg text-sm transition-colors ${
                    selectedLeague === league.id 
                      ? 'bg-[#00E599]/20 border border-[#00E599]/50' 
                      : 'hover:bg-zinc-800'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <span>{league.flag}</span>
                    <span className={`${!lc?.enabled ? 'text-zinc-500 line-through' : 'text-white'}`}>
                      {league.name}
                    </span>
                  </div>
                  <div className="flex items-center gap-1">
                    <span className={`font-mono text-xs ${getExigenciaColor(lc?.exigencia_base || 100)}`}>
                      {lc?.exigencia_base || 100}%
                    </span>
                    {lc?.exigencia_base > 100 && <TrendingUp className="h-3 w-3 text-red-400" />}
                    {lc?.exigencia_base < 100 && <TrendingDown className="h-3 w-3 text-green-400" />}
                  </div>
                </button>
              );
            })}
          </CardContent>
        </Card>

        {/* Configuration */}
        <div className="lg:col-span-2 space-y-4">
          {/* League Config */}
          <Card className="bg-zinc-900/50 border-zinc-800">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <span className="text-xl">{LEAGUES.find(l => l.id === selectedLeague)?.flag}</span>
                  <span className="text-white font-medium">
                    {LEAGUES.find(l => l.id === selectedLeague)?.name}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-zinc-400">Activa</span>
                  <Switch
                    checked={currentLeague?.enabled ?? true}
                    onCheckedChange={(checked) => updateLeagueEnabled(selectedLeague, checked)}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-xs text-zinc-400">Exigencia base</span>
                  <span className={`text-sm font-mono font-bold ${getExigenciaColor(currentLeague?.exigencia_base || 100)}`}>
                    {currentLeague?.exigencia_base || 100}%
                  </span>
                </div>
                <Slider
                  value={[currentLeague?.exigencia_base || 100]}
                  onValueChange={([value]) => updateLeagueExigencia(selectedLeague, value)}
                  min={50}
                  max={150}
                  step={5}
                />
                <div className="flex justify-between text-[10px] text-zinc-500">
                  <span>50%</span>
                  <span>100%</span>
                  <span>150%</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Markets */}
          <Card className="bg-zinc-900/50 border-zinc-800">
            <CardHeader className="py-3 px-4">
              <CardTitle className="text-sm text-white">Mercados</CardTitle>
            </CardHeader>
            <CardContent className="p-3">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {MARKETS.map(market => {
                  const mc = currentLeague?.markets?.[market.id];
                  return (
                    <div 
                      key={market.id}
                      className={`p-3 rounded-lg border ${
                        mc?.enabled ? 'bg-zinc-800/50 border-zinc-700' : 'bg-zinc-900/50 border-zinc-800 opacity-50'
                      }`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <span>{market.icon}</span>
                          <span className="text-white text-sm">{market.name}</span>
                        </div>
                        <Switch
                          checked={mc?.enabled ?? true}
                          onCheckedChange={(checked) => updateMarketConfig(selectedLeague, market.id, 'enabled', checked)}
                        />
                      </div>

                      {mc?.enabled && (
                        <div className="space-y-2">
                          <div className="flex justify-between items-center">
                            <span className="text-[10px] text-zinc-500">Exigencia</span>
                            <span className={`text-xs font-mono ${getExigenciaColor(mc?.exigencia || 100)}`}>
                              {mc?.exigencia || 100}%
                            </span>
                          </div>
                          <Slider
                            value={[mc?.exigencia || 100]}
                            onValueChange={([value]) => updateMarketConfig(selectedLeague, market.id, 'exigencia', value)}
                            min={50}
                            max={150}
                            step={5}
                            className="h-1"
                          />
                          <div className="grid grid-cols-2 gap-2 mt-2">
                            <div>
                              <label className="text-[10px] text-zinc-500">Min Odds</label>
                              <input
                                type="number"
                                step="0.05"
                                min="1.20"
                                max="3.00"
                                value={mc?.min_odds || 1.55}
                                onChange={(e) => updateMarketConfig(selectedLeague, market.id, 'min_odds', parseFloat(e.target.value))}
                                className="w-full bg-zinc-900 border border-zinc-700 rounded px-2 py-1 text-white text-xs"
                              />
                            </div>
                            <div>
                              <label className="text-[10px] text-zinc-500">Min Value %</label>
                              <input
                                type="number"
                                step="1"
                                min="0"
                                max="30"
                                value={mc?.min_value || 7}
                                onChange={(e) => updateMarketConfig(selectedLeague, market.id, 'min_value', parseInt(e.target.value))}
                                className="w-full bg-zinc-900 border border-zinc-700 rounded px-2 py-1 text-white text-xs"
                              />
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
